import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { supabase } from '@/lib/supabase'
import type { ChamberRow, ChamberUpdate, UseChamberReturn } from '@/types'

// ...existing code...

export const useChamber = (): UseChamberReturn => {
  const { user } = useAuth()
  const [chamber, setChamber] = useState<ChamberRow | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  const fetchChamber = useCallback(async () => {
    if (!user) return
    setIsLoading(true)
    try {
      // Use RPC function to get user's chamber safely
      const { data: chamberData, error } =
        await supabase.rpc('get_user_chamber')

      if (error) throw error

      if (chamberData.length > 0) {
        setChamber(chamberData[0] ?? undefined)
      } else {
        setChamber(undefined)
      }
    } catch (error) {
      console.error('Error fetching chamber:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  const createChamber = async (
    name: string,
    { ...details }: ChamberUpdate = {},
  ): Promise<{ data: ChamberRow | undefined; error: string | undefined }> => {
    if (!user) {
      return { data: undefined, error: 'Not authenticated' }
    }

    try {
      const { data: professional } = await supabase
        .from('professionals')
        .select('id')
        .eq('user_id', user.id)
        .single()

      if (!professional) throw new Error('Professional profile not found')

      const { data: newChamber, error: chamberError } = await supabase
        .from('chambers')
        .insert({
          name,
          owner_id: user.id,
          ...details,
        })
        .select()
        .single()

      if (chamberError) throw chamberError

      const { error: updateError } = await supabase
        .from('professionals')
        .update({ chamber_id: newChamber.id, role: 'admin', status: 'active' })
        .eq('user_id', user.id)

      if (updateError) throw updateError

      setChamber(newChamber)

      return { data: newChamber, error: undefined }
    } catch (error: unknown) {
      console.error('Error creating chamber:', error)
      const message =
        error instanceof Error ? error.message : 'Unknown error occurred'

      return { data: undefined, error: message }
    }
  }

  useEffect(() => {
    fetchChamber().catch((error) => {
      console.error('Failed to fetch chamber in effect:', error)
    })
  }, [fetchChamber])

  return { chamber, createChamber, isLoading, refreshChamber: fetchChamber }
}
